import { Goals } from "@/clients/models/Goals";
import { StepsButtons } from "@/components/button/steps-button";
import { ProfilePageContainer } from "@/components/container/profile-page-container";
import { Option, OptionPicker } from "@/components/form/option-picker";
import { PageTitleComponent } from "@/components/page-title";
import { LocalStorageKeys } from "@/constants/local-storage-keys";
import { QueryKeys } from "@/constants/query-keys";
import { useGetGoals } from "@/hooks/requests/goals/use-get-goals";
import { useUpdateGoals } from "@/hooks/requests/goals/use-update-goals";
import { useToast } from "@/hooks/ui/use-toast";
import { useStepRouteInfo } from "@/hooks/use-step-route-info";
import { ProfilingGoalsSchema } from "@/schemas/user-schema";
import { setLocalStorageJson } from "@/utils/local-storage";
import { setProfileContext } from "@/utils/sentry/tools";
import { IonSkeletonText, useIonRouter } from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";

const goalsOptions: Option<string>[] = [
  {
    label: "Gain Weight",
    value: "gain-weight",
  },
  {
    label: "Lose Weight",
    value: "lose-weight",
  },
  {
    label: "Maintain Weight",
    value: "maintain-weight",
  },
] as const;

const ProfilingGoalPage = () => {
  const { info } = useStepRouteInfo();
  const navigation = useIonRouter();
  const queryClient = useQueryClient();
  const { goals, isLoading } = useGetGoals();
  const { updateGoals, isUpdating } = useUpdateGoals();
  const { showError } = useToast();
  if (isLoading) {
    return <IonSkeletonText animated />;
  }

  console.log("goals", goals);

  const initialValues: Pick<Goals, "goalsWithNutriCoach"> = {
    goalsWithNutriCoach:
      goals?.goalsWithNutriCoach !== undefined
        ? goals.goalsWithNutriCoach.toString()
        : "lose-weight",
  };

  async function handleSubmit({
    goalsWithNutriCoach,
  }: {
    goalsWithNutriCoach: string;
  }) {
    const formData = await ProfilingGoalsSchema.validate({
      goalsWithNutriCoach,
    });

    const dataBody: Goals = {
      goalsWithNutriCoach: formData.goalsWithNutriCoach,
      specificWeightGoals: goals?.specificWeightGoals ?? "lose-weight",
    };

    updateGoals(dataBody, {
      onSuccess: () => {
        setLocalStorageJson(
          LocalStorageKeys.USER_STATS_KEY,
          JSON.stringify(dataBody),
        );
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.GOALS],
        });
        setProfileContext(QueryKeys.GOALS, dataBody);
        navigation.push(info?.nextPath ?? "", "none");
      },
      onError: (error) => {
        showError(error.message);
      },
    });
  }

  return (
    <ProfilePageContainer>
      <PageTitleComponent
        title="Your Goal?"
        description="Short description goes here..."
        className="mt-2 mb-6 w-full"
      />

      <Formik
        initialValues={initialValues}
        validateOnMount={true}
        validateOnChange={true}
        validateOnBlur={true}
        validationSchema={ProfilingGoalsSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, validateField }) => {
          return (
            <Form className="w-full">
              <div className="flex flex-col justify-between items-center h-full w-full gap-16">
                <OptionPicker
                  name="goalsWithNutriCoach"
                  values={values}
                  options={goalsOptions}
                  setFieldValue={setFieldValue}
                  validateField={validateField}
                />
              </div>

              <StepsButtons
                previousPath={info?.previousPath}
                type="submit"
                disabled={isUpdating}
              />
            </Form>
          );
        }}
      </Formik>
    </ProfilePageContainer>
  );
};

export default ProfilingGoalPage;
